import { EventEmitter, Watchdog } from '@timing71/common';
import { getSportItemSet, sessionQuery, sessionLayoutQuery, sessionSubscription } from './graphql/index.js';
import { createClient } from 'graphql-ws';
import { makeWrappedWebsocket } from './socket.js';

const GQL_SERVICE_URL = "https://wec-service-platform.sportall.tv/graphql";
const GQL_TIMING_URL = "https://live-timing-api.sportall.tv/graphql";

const MINUTES = 1000 * 60;

// Exported for tests
export const pickAppropriateSession = (sessions, referencePoint=Date.now()) => {
  // This method is basically a big workaround for the fact that they often forget
  // to update a session to `closed: true` - often in quali - meaning that we can
  // get fixated on a past session (or snap back to it at the expiration of the race time).

  // In the middle of a session, the logic is easy...
  const currentSession = sessions.find(
    s => (
      new Date(s.startsAt).getTime() <= referencePoint &&
      new Date(s.endsAt).getTime() >= referencePoint
    )
  );

  if (currentSession) {
    return currentSession;
  }


  // make sure they're sorted
  const remainingSessions = [...sessions.sort((a, b) => b.startsAt.localeCompare(a.startsAt))];
  let pickedSession = remainingSessions[0];

  while (remainingSessions.length > 0) {
    const candidate = remainingSessions.pop();
    const start = new Date(candidate.startsAt).getTime();
    const end = new Date(candidate.endsAt).getTime();


    if (start <= referencePoint) {
      pickedSession = candidate;
    }
    else if (

      pickedSession?.endsAt < candidate.startsAt &&
      start < referencePoint + 15 * MINUTES
    ) {
      pickedSession = candidate;
    }
  }

  return pickedSession;

}

export class Client extends EventEmitter {
  constructor(connectionService, sessionData = {}, bestSectors = {}, raceControlPrevTimestamp = 0, refData = {}) {
    super();
    this.connectionService = connectionService;

    this.sessionData = sessionData;
    this.data = {};
    this.bestSectors = bestSectors;
    this.raceControlPrevTimestamp = raceControlPrevTimestamp;
    this.race = {};
    this.refData = refData;

    this._prevSessionIDs = [];

    this.start = this.start.bind(this);
    this.queryGraphQL = this.queryGraphQL.bind(this);
  }

  async start() {

    try {
      if (!this.sessionData?.id) {

        const runningItems = await this.queryGraphQL(GQL_SERVICE_URL, getSportItemSet('running'));
        const runningItem = runningItems?.sportItemSets?.sportItemSets?.[0];

        if (runningItem?.liveStatsUrl) {
          console.debug(`Found running session: ${runningItem.subtitle} - ${runningItem.title} (${runningItem.liveStatsUrl})`);
          await this.chooseSession(runningItem);
        }
        else {
          const upcomingItems = await this.queryGraphQL(GQL_SERVICE_URL, getSportItemSet('notStarted'));
          const nextItem = upcomingItems?.sportItemSets?.sportItemSets?.[0];
          if (nextItem?.liveStatsUrl) {
            console.debug(`Found upcoming session: ${nextItem.subtitle} - ${nextItem.title} (${nextItem.liveStatsUrl})}`);
            await this.chooseSession(nextItem);
          }
          else {
            throw new Error('Unable to find any live sessions!');
          }
        }
      }

      await this.begin();

    }
    catch (e) {
      this.emit('error', e);
      this._sessionTimeout = setTimeout(this.start, 5000);
    }

  }

  async chooseSession(liveItem) {

    const { liveStatsUrl } = liveItem;
    const url = new URL(liveStatsUrl);

    const raceID = url.pathname.split('/').slice(-1)[0];
    console.debug(`Checking active sessions for race ID ${raceID}`);

    const raceSessions = await this.queryGraphQL(
      GQL_TIMING_URL,
      JSON.stringify({
        query: sessionLayoutQuery,
        variables: {
          raceId: raceID
        }
      })
    );

    if (raceSessions?.race) {
      this.race = raceSessions.race;
    }

    if (liveItem.subtitle.endsWith('ELMS')) {
      this.race.series = "European Le Mans Series";
    }
    else if (liveItem.subtitle.endsWith('Le Mans Cup') || liveItem.subtitle.endsWith('MLMC')) {
      this.race.series = "Le Mans Cup";
    }

    const currentSession = pickAppropriateSession(
      raceSessions?.race?.sessions,
      Date.now()
    );
    const ecmSessionId = currentSession?.id;

    if (ecmSessionId && ecmSessionId !== this.sessionData.id) {
      console.debug("Found new session, ID:", ecmSessionId);

      if (ecmSessionId in this._prevSessionIDs) {
        // Sometimes previously-closed sessions can be reported as "open" again
        // by upstream; let's ignore what is probably stale cached data
        console.debug("Ignoring session as it has previously been live");
      }
      else {
        const isSessionChange = !!this.sessionData.id;

        try {
          const sessionDataText = await this.connectionService.fetch(`https://race-data-manager.sportall.tv/session/${ecmSessionId}`);
          this.sessionData = JSON.parse(sessionDataText);
          this.sessionData.subtitle = liveItem.subtitle;
        }
        catch (e) {
          console.warn("Error fetching session data", e);
          // We'll have to fill in the gaps ourselves later
          this.sessionData = { id: ecmSessionId };
        }

        if (isSessionChange) {
          this.stop();
          this.emit('session_change');
          this.bestSectors = {};
          await this.begin();
        }

        this._prevSessionIDs.push(ecmSessionId);

        try {
          const raceID = raceSessions.race.id;
          const refDataText = await this.connectionService.fetch(`https://storage.googleapis.com/ecm-prod/assets/live/${raceID}.json`);
          this.refData = JSON.parse(refDataText);
        }
        catch (e) {
          console.warn("Failed to fetch reference data", e);
        }

      }

    }
    else if (!ecmSessionId && !this.sessionData.id) {
      // This will be caught in start() and we will retry
      throw new Error('Could not identify a session ID');
    }

    this._sessionTimeout = setTimeout(
      () => this.chooseSession(liveItem),
      60000
    );
  }

  async begin() {
    const initialState = await this._getInitialState(this.sessionData.id);

    if (initialState) {
      this.data = initialState;
      this.emit('update');
    }

    this._beginGQLSubscription();
  }

  _beginGQLSubscription() {
    const wws = makeWrappedWebsocket(this.connectionService);

    const gqlClient = createClient({
      url: 'wss://live-timing-api.sportall.tv/graphql',
      webSocketImpl: wws
    });

    const watchdog = new Watchdog(60000);

    watchdog.on('timeout', () => {
      // Force connection to close - GQLClient will automatically reconnect
      gqlClient.terminate();
    })

    gqlClient.on('opened', () => {
      this.emit('connected');
      watchdog.start();
    });
    gqlClient.on('closed', () => {
      this.emit('disconnected');
      watchdog.stop();
    });

    this._closeSubscription = gqlClient.subscribe({
      query: sessionSubscription,
      variables: {
        sessionId: this.sessionData.id
      }
    }, {
      complete() {},
      error: (e) => {
        console.error(e);
        this.emit('error', e);
        this._beginGQLSubscription();
      },
      next: (data) => {
        watchdog.dataReceived();
        this.data = {
          ...data.data.session,
          date: Date.now()
        };
        this.emit('update');

        const newRCMessages = (data.data.session?.raceControl?.messages || []).filter(
          m => m.dayTime > this.raceControlPrevTimestamp
        );
        if (newRCMessages.length > 0) {
          this.emit('race_control', newRCMessages);
          this.raceControlPrevTimestamp = Math.max(...newRCMessages.map(m => m.dayTime)) || Date.now();
        }

      }
    });
  }

  async _getInitialState(sessionID) {
    const state = await this.queryGraphQL(
      GQL_TIMING_URL,
      JSON.stringify({
        operationName: "Session",
        variables: {
          sessionId: sessionID
        },
        query: sessionQuery
      })
    )

    return state?.session;
  }

  async queryGraphQL(url, queryBody) {
    try {
      const text = await this.connectionService.fetch(
        url,
        {
          headers: {
            "content-type": "application/json",
            "x-application-id": "fiawec-webapp-hkMleUoNg8I8uaXC",
            "Accept-Language": "en",
          },
          body: queryBody,
          method: "POST"
        }
      );

      return JSON.parse(text)?.data;
    }
    catch (e) {
      console.warn('Error parsing GQL response', e);
      return null;
    }
  }

  stop() {
    this._closeSubscription && this._closeSubscription();
    this._sessionTimeout && clearTimeout(this._sessionTimeout);
  }
}
